/**
 * Business Detail Page - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Light ops mode.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { apiClient } from '../api/apiConfig';
import { Business } from '../types/business';
import RelatedItemsTabs from '../components/ui/RelatedItemsTabs';
import JournalForm from '../components/forms/JournalForm';
import { BusinessValuation } from '../components/BusinessValuation';
import { useJournalModal } from '../hooks/useJournalModal';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { Panel, Badge } from '../design/primitives';

const BusinessDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingTaskId, setUpdatingTaskId] = useState<number | null>(null);

  const fetchBusiness = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/api/businesses/${id}/`);
      setBusiness(response.data);
    } catch (err) {
      console.error("Error fetching business:", err);
      setError("Failed to load business details.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const {
    isJournalModalOpen,
    selectedJournalEntry,
    handleJournalEntryClick,
    handleJournalEntryUpdated,
    handleCloseJournal,
  } = useJournalModal(fetchBusiness);

  useEffect(() => {
    if (id) {
      fetchBusiness();
    }
  }, [id, fetchBusiness]);

  const handleTaskToggle = async (taskId: number, currentStatus: boolean) => {
    setUpdatingTaskId(taskId);
    try {
      await apiClient.patch(`/api/tasks/${taskId}/`, { is_done: !currentStatus });
      await fetchBusiness();
    } catch (err) {
      console.error('Failed to update task:', err);
    } finally {
      setUpdatingTaskId(null);
    }
  };

  if (loading) {
    return (
      <div style={{
        backgroundColor: COLORS.bg.base,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: SPACING[24]
      }}>
        <ArrowPathIcon style={{
          width: '24px',
          height: '24px',
          color: COLORS.text.tertiary,
          animation: 'spin 1s linear infinite'
        }} />
      </div>
    );
  }

  if (error || !business) {
    return (
      <div style={{
        backgroundColor: COLORS.bg.base,
        minHeight: '100vh',
        padding: SPACING[24]
      }}>
        <Panel>
          <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
            <ExclamationTriangleIcon style={{
              width: '20px',
              height: '20px',
              color: COLORS.status.error,
              flexShrink: 0
            }} />
            <p style={{
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.default,
              color: COLORS.status.error,
              margin: 0
            }}>
              {error || "Business not found."}
            </p>
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: COLORS.bg.base,
      minHeight: '100vh',
      padding: SPACING[24]
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: SPACING[24],
        marginBottom: SPACING[24]
      }}>
        {/* Main Description Card */}
        <Panel>
          <h1 style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h1,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            margin: 0,
            marginBottom: SPACING[8]
          }}>
            {business.name}
          </h1>

          {business.description && (
            <p style={{
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.default,
              color: COLORS.text.secondary,
              margin: 0,
              marginTop: SPACING[8],
              lineHeight: '1.6'
            }}>
              {business.description}
            </p>
          )}

          <div style={{ marginTop: SPACING[16] }}>
            <Badge color={business.is_active ? 'success' : 'error'}>
              {business.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </Panel>

        {/* Valuation Card */}
        <div>
          <BusinessValuation business={business} onUpdate={fetchBusiness} />
        </div>
      </div>

      {/* Related Items Tabs */}
      <RelatedItemsTabs
        tasks={business.tasks}
        projects={business.projects}
        goals={business.goals}
        emails={business.emails}
        meetings={business.meetings}
        journalEntries={business.journal_entries}
        people={business.people}
        onTaskToggle={handleTaskToggle}
        onJournalEntryClick={handleJournalEntryClick}
        isUpdatingTask={updatingTaskId}
      />

      {isJournalModalOpen && selectedJournalEntry && (
        <JournalForm
          isOpen={isJournalModalOpen}
          onClose={handleCloseJournal}
          onJournalEntryCreated={handleJournalEntryUpdated}
          onJournalEntryUpdated={handleJournalEntryUpdated}
          initialEntry={selectedJournalEntry}
          isEditMode={true}
        />
      )}
    </div>
  );
};

export default BusinessDetailPage;
